import { AxiosError } from 'axios';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { AbsenceFilter, AbsencePagination, AbsenceRecord, CreateAbsenceData, ReviewAbsenceData, UpdateAbsenceData } from '../../types';
import AbsenceService from '../services/AbsenceService';
import { CACHE_KEY_ABSENCES } from '../constants';

const useGetAbsencesPagination = (filter: AbsenceFilter) => {
    return useInfiniteQuery<AbsencePagination, AxiosError<any>>({
        queryKey: [...CACHE_KEY_ABSENCES, filter],
        queryFn: ({ pageParam = filter }) => AbsenceService.getAbsencesPaginated(pageParam),
        getNextPageParam: (lastPage) => {
            if (lastPage.meta.current_page === lastPage.meta.last_page) {
                return undefined;
            }

            return {
                ...filter,
                page: lastPage.meta.current_page + 1,
            };
        },
    });
};

const useGetAbsenceById = (id?: number | null) => {
    return useQuery<AbsenceRecord, AxiosError<any>>({
        queryFn: () => AbsenceService.getAbsenceById(id!),
        queryKey: ['absence', id],
        enabled: !!id,
    });
};

const useCreateAbsence = (onSuccess?: (data: AbsenceRecord) => void, onError?: (error: AxiosError<any>) => void) => {
    const queryClient = useQueryClient();
    return useMutation<AbsenceRecord, AxiosError, CreateAbsenceData>({
        mutationFn: AbsenceService.createAbsence,
        onSuccess: (data) => {
            queryClient.invalidateQueries(CACHE_KEY_ABSENCES);
            onSuccess && onSuccess(data);
        },
        onError: onError,
    });
};

const useUpdateAbsence = (onSuccess?: (data: AbsenceRecord) => void, onError?: (error: AxiosError<any>) => void) => {
    const queryClient = useQueryClient();
    return useMutation<AbsenceRecord, AxiosError, { id: number; data: UpdateAbsenceData }>({
        mutationFn: ({ id, data }) => AbsenceService.updateAbsence(id, data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(CACHE_KEY_ABSENCES);
            onSuccess && onSuccess(data);
        },
        onError: onError,
    });
};

const useCancelAbsence = (onSuccess?: () => void, onError?: (error: AxiosError<any>) => void) => {
    const queryClient = useQueryClient();
    return useMutation<void, AxiosError, number>({
        mutationFn: (id) => AbsenceService.cancelAbsence(id),
        onSuccess: () => {
            queryClient.invalidateQueries(CACHE_KEY_ABSENCES);
            onSuccess && onSuccess();
        },
        onError: onError,
    });
};

const useReviewAbsence = (onSuccess?: (data: AbsenceRecord) => void, onError?: (error: AxiosError<any>) => void) => {
    const queryClient = useQueryClient();
    return useMutation<AbsenceRecord, AxiosError, { id: number; data: ReviewAbsenceData }>({
        mutationFn: ({ id, data }) => AbsenceService.reviewAbsence(id, data),
        onSuccess: (data) => {
            queryClient.invalidateQueries(CACHE_KEY_ABSENCES);
            onSuccess && onSuccess(data);
        },
        onError: onError,
    });
};

export { useGetAbsencesPagination, useGetAbsenceById, useCreateAbsence, useUpdateAbsence, useCancelAbsence, useReviewAbsence };
